<script setup lang="ts">
  defineOptions({ name: 'EventDetailSectionNav' })

  import { computed, type PropType } from 'vue'
  import { useI18n } from 'vue-i18n'

  import { createElementId } from '@common/core-layer/utils'

  import CPTabPane from '@ui/components/CPTabPane.vue'
  import CPTabs from '@ui/components/CPTabs.vue'

  import type { EventDetailNavTab, EventDetailSectionId } from '../types/event-detail'

  const props = defineProps({
    tabs: {
      type: Array as PropType<readonly EventDetailNavTab[]>,
      required: true,
    },
    activeTabId: {
      type: String as PropType<EventDetailSectionId>,
      required: true,
    },
    idPrefix: {
      type: String,
      default: 'event-detail',
    },
  })

  const emit = defineEmits<{
    selectTab: [tabId: EventDetailSectionId]
  }>()

  const { t } = useI18n()

  const tabItems = computed(() =>
    props.tabs.map(tab => ({
      id: tab.id,
      label: t(tab.labelKey),
    }))
  )

  const handleUpdateValue = (value: string | number) => {
    emit('selectTab', String(value) as EventDetailSectionId)
  }
</script>

<template>
  <nav
    :id="createElementId(idPrefix, 'section-nav', 'root')"
    class="event-detail-section-nav flex h-12 min-w-0 flex-1 items-center overflow-x-auto"
    :aria-label="t('clientportal.events.detail.nav.ariaLabel')"
  >
    <CPTabs
      :id="createElementId(idPrefix, 'section-nav', 'tabs')"
      type="line"
      size="lg"
      justify-content="start"
      :value="activeTabId"
      :max-visible-tabs="tabItems.length"
      @update:value="handleUpdateValue"
    >
      <CPTabPane v-for="item in tabItems" :key="item.id" :name="item.id" :tab="item.label" :tab-key="item.id">
        <div />
      </CPTabPane>
    </CPTabs>
  </nav>
</template>

<style scoped>
  .event-detail-section-nav :deep([class*='-tabs-pane-wrapper']) {
    display: none;
  }

  .event-detail-section-nav :deep([class*='-tabs-nav']) {
    margin-bottom: 0;
  }

  .event-detail-section-nav {
    scrollbar-width: none;
  }

  .event-detail-section-nav::-webkit-scrollbar {
    display: none;
  }
</style>
